import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, ViewChild } from '@angular/core';
import { PaginatorFetcher, PaginatorFilters, TableComponent, TableConfig } from '@michelin/theme';
import { FormBuilder, FormGroup } from '@angular/forms';
import { map, Observable } from 'rxjs';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { ProductDataService } from '../../services/product-data.service';
import { Store } from '@ngxs/store';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { FactoryCodeFiltersResponse, Pagination } from '../../models/response.model';
import { FactoryCode } from '../../models/factory-code.model';
import { ExportResponse } from '../../../../shared/services/export-to-excel.service';
import { DealerState } from '../../../../shared/state/DealerState';
import { MatDialog } from '@angular/material/dialog';
import { ExportModalComponent, ExportModalData } from '../../../../shared/components/export-modal/export-modal.component';
import { ExportColumnDef } from '../../../../shared/models/export-column-def.model';

@Component({
  selector: 'app-factory-code',
  templateUrl: './factory-code.component.html',
  styleUrl: './factory-code.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FactoryCodeComponent implements OnInit {
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<FactoryCode>;
  public fb = inject(FormBuilder);
  public store = inject(Store);
  public translationService = inject(TranslationService);
  public productDataService = inject(ProductDataService);
  public cdr = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);

  factoryCodeList: string[] = [];
  factoryNameList: string[] = [];
  factoryTypeList: string[] = ['R', 'F'];

  filterForm: FormGroup;
  filterFormBackend: FormGroup;

  factoryCodes: FactoryCode[] = [];
  pagination: Pagination;
  pageSizeOptions = [10, 25, 50, 100];
  currentPage = 1;
  pageSize = 10;

  openExportModal(): void {
    const columns: ExportColumnDef[] = Object.entries(this.columnNames).map(([key, label]) => ({ key, label, selected: true }));
    const data: ExportModalData = {
      columns,
      dataFetcher: this.exportDataFetcher,
      fileNamePrefix: this.dealerName + '-FactoryCode-export',
      maxExportRows: 10000
    };
    this.dialog.open(ExportModalComponent, { data, width: '480px' });
  }

  tableConfig: TableConfig<any> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom'
    },
    columns: {}
  };
  dealerName = this.store.selectSnapshot(DealerState.currentDealer).name;
  columnNames: Record<string, string> = {};

  exportDataFetcher: () => Observable<ExportResponse> = () =>
    this.productDataService.getFactoryCode(this.filterFormBackend.value, 1, 10000).pipe(
      map((response: any) => ({
        data: (response.data.factoryCodes as FactoryCode[]).map((item) => ({
          factoryCode: item.factoryCode,
          factoryName: item.factoryName,
          factoryType: item.factoryType
        }))
      }))
    );

  fetcher: PaginatorFetcher<any> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    this.generateFilterForm();

    return this.productDataService.getFactoryCode({ ...this.filterFormBackend.value, ...filters }, filters.page, filters.limit).pipe(
      map((response: any) => {
        this.factoryCodes = response.data.factoryCodes || [];
        this.pagination = response.data.pagination;
        return { items: this.factoryCodes, count: this.pagination.totalRecords };
      })
    );
  };

  constructor() {
    this.filterForm = this.fb.group({
      factoryCode: [''],
      factoryName: [''],
      factoryType: ['']
    });
    this.filterFormBackend = this.fb.group({
      factoryCode: [''],
      factoryName: [''],
      factoryType: ['']
    });
  }

  ngOnInit(): void {
    this.productDataService.getFactoryCodeFilters().subscribe((response: FactoryCodeFiltersResponse) => {
      this.factoryCodeList = response.data.factoryCodes || [];
      this.factoryNameList = response.data.factoryNames || [];
      this.cdr.markForCheck();
    });
    this.filterForm.valueChanges.pipe(distinctUntilChanged(), debounceTime(800)).subscribe(() => {
      this.generateFilterForm();
      this.tableRef?.patchExtraFilters(this.filterFormBackend.value, false);
    });

    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.columnNames = {
        factoryCode: this.translationService.translate('factoryCodeText'),
        factoryName: this.translationService.translate('factoryNameText'),
        factoryType: this.translationService.translate('factoryTypeText')
      };

      this.tableConfig.columns = {
        factoryCode: { cell: (row) => row.factoryCode, header: this.translationService.translate('factoryCodeText') },
        factoryName: { cell: (row) => row.factoryName, header: this.translationService.translate('factoryNameText') },
        factoryType: { cell: (row) => row.factoryType, header: this.translationService.translate('factoryTypeText') }
      };
      this.cdr.markForCheck();
    });
  }

  clearFilters(): void {
    this.filterForm.reset({
      factoryCode: '',
      factoryName: '',
      factoryType: ''
    });
  }

  private generateFilterForm(): void {
    const filterCode = this.filterForm.get('factoryCode').value;
    const filterName = this.filterForm.get('factoryName').value;
    const filterType = this.filterForm.get('factoryType').value;
    this.filterFormBackend.patchValue({
      factoryCode: filterCode.length > 0 && filterCode.length === this.factoryCodeList.length ? [] : filterCode,
      factoryName: filterName.length > 0 && filterName.length === this.factoryNameList.length ? [] : filterName,
      factoryType: filterType.length > 0 && filterType.length === this.factoryTypeList.length ? [] : filterType
    });
  }
}
